import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  VideoLibrary as VideoIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  History as HistoryIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  Document,
  DocumentCategory,
  DocumentStatus,
  DocumentAccess,
  getCategoryLabel,
  getStatusLabel,
  getAccessLabel,
  formatFileSize,
  getFileIcon,
} from '../services/mockKnowledge';
import { api } from '../services/api';

const KnowledgeBase: React.FC = () => {
  const { user, session } = useAuth();
  const { hasPermission, isAdmin, isAgence, isSOBEBRA } = usePermissions();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | 'all'>('all');
  const [folderFilter, setFolderFilter] = useState<string | 'all'>('all');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    category: 'other' as DocumentCategory,
    folder: '',
    access_level: 'all' as DocumentAccess,
    tags: '',
  });
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'other' as DocumentCategory,
    folder: '',
    access_level: 'all' as DocumentAccess,
    tags: '',
    file: null as File | null,
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (session && user) {
      loadDocuments();
    }
  }, [session, user]);

  useEffect(() => {
    filterDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, categoryFilter, folderFilter, user, documents]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.knowledge.getAll();
      // Transformer les documents de l'API en format Document
      const transformedDocs: Document[] = (response.documents || []).map((doc: any) => ({
        id: doc.id,
        title: doc.title,
        description: doc.description,
        category: doc.category as DocumentCategory,
        folder: doc.folder,
        status: (doc.status === 'pending' ? 'approved' : doc.status) as DocumentStatus, // Traduire 'pending' en 'approved' pour l'affichage
        access_level: doc.access_level as DocumentAccess,
        tags: doc.tags || [],
        created_by: doc.uploaded_by || '',
        created_by_name: doc.uploaded_by_user?.name || '',
        created_at: doc.created_at,
        updated_at: doc.updated_at,
        approved_by: doc.approved_by,
        approved_by_name: doc.approved_by_user?.name,
        approved_at: doc.approved_at,
        download_count: doc.download_count || 0,
        versions: [{
          id: doc.id + '-v1',
          version: '1.0',
          file_name: doc.file_name,
          file_size: doc.file_size || 0,
          file_type: doc.file_type || 'application/octet-stream',
          file_url: doc.file_path || '',
          uploaded_by: doc.uploaded_by || '',
          uploaded_by_name: doc.uploaded_by_user?.name || '',
          uploaded_at: doc.created_at,
          is_current: true,
          change_log: 'Version initiale',
        }],
        current_version: {
          id: doc.id + '-v1',
          version: '1.0',
          file_name: doc.file_name,
          file_size: doc.file_size || 0,
          file_type: doc.file_type || 'application/octet-stream',
          file_url: doc.file_path || '',
          uploaded_by: doc.uploaded_by || '',
          uploaded_by_name: doc.uploaded_by_user?.name || '',
          uploaded_at: doc.created_at,
          is_current: true,
          change_log: 'Version initiale',
        },
      }));
      setDocuments(transformedDocs);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    // Filtrer par accès
    filtered = filtered.filter((doc) => {
      if (isAdmin) return true;
      if (doc.access_level === 'all') return true;
      if (doc.access_level === 'agence' && (isAgence || isAdmin)) return true;
      if (doc.access_level === 'soebra' && (isSOBEBRA || isAdmin)) return true;
      return false;
    });

    // Filtrer uniquement les documents approuvés pour les non-admins
    if (!isAdmin && !isAgence) {
      filtered = filtered.filter((doc) => doc.status === 'approved');
    }

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(
        (doc) =>
          doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtre par catégorie
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((doc) => doc.category === categoryFilter);
    }

    // Filtre par dossier
    if (folderFilter !== 'all') {
      filtered = filtered.filter((doc) => doc.folder === folderFilter);
    }

    setFilteredDocuments(filtered);
  };

  const getFolders = (): string[] => {
    const folders = new Set<string>();
    documents.forEach((doc) => {
      if (doc.folder) folders.add(doc.folder);
    });
    return Array.from(folders).sort();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, docId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocId(docId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocId(null);
  };

  const handleViewDetails = (doc: Document) => {
    setSelectedDocument(doc);
    setDetailDialogOpen(true);
    handleMenuClose();
  };

  const handleDownload = (doc: Document) => {
    // Simuler le téléchargement
    const link = document.createElement('a');
    link.href = doc.current_version.file_url;
    link.download = doc.current_version.file_name;
    link.click();
    handleMenuClose();
  };

  const handleViewVersions = (doc: Document) => {
    setSelectedDocument(doc);
    setVersionDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = (doc: Document) => {
    setSelectedDocument(doc);
    setEditForm({
      title: doc.title,
      description: doc.description || '',
      category: doc.category,
      folder: doc.folder || '',
      access_level: doc.access_level,
      tags: doc.tags.join(', '),
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleUpdateDocument = async () => {
    if (!selectedDocument) return;

    try {
      setUploading(true);
      await api.knowledge.update(selectedDocument.id, {
        title: editForm.title,
        description: editForm.description || undefined,
        category: editForm.category,
        folder: editForm.folder || undefined,
        access_level: editForm.access_level,
        tags: editForm.tags
          ? editForm.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
          : [],
      });
      await loadDocuments();
      setEditDialogOpen(false);
      setSelectedDocument(null);
      alert('Document modifié avec succès');
    } catch (error: any) {
      console.error('Erreur lors de la modification:', error);
      alert('Erreur lors de la modification: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.')) {
      return;
    }

    try {
      await api.knowledge.delete(docId);
      await loadDocuments();
      handleMenuClose();
      alert('Document supprimé avec succès');
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const canManageDocuments = () => {
    return isAdmin || isAgence || isSOBEBRA;
  };

  const getDocumentsByCategory = (category: DocumentCategory) => {
    return filteredDocuments.filter((doc) => doc.category === category).length;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Base de Connaissances et Documentation
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Référentiel unique pour les documents de travail et les directives de marque
            </Typography>
          </Box>
          {canManageDocuments() && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Uploader un Document
            </Button>
          )}
        </Box>

        {/* Statistiques */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Documents
                </Typography>
                <Typography variant="h4">{filteredDocuments.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Guidelines
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {getDocumentsByCategory('guidelines')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Modèles
                </Typography>
                <Typography variant="h4" color="success.main">
                  {getDocumentsByCategory('templates')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Procédures
                </Typography>
                <Typography variant="h4" color="info.main">
                  {getDocumentsByCategory('procedures')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtres et recherche */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Rechercher dans les documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Catégorie"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as DocumentCategory | 'all')}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">Toutes les catégories</option>
                <option value="guidelines">Guidelines de Marque</option>
                <option value="templates">Modèles</option>
                <option value="procedures">Procédures</option>
                <option value="pricing">Tarifs</option>
                <option value="other">Autre</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Dossier"
                value={folderFilter}
                onChange={(e) => setFolderFilter(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">Tous les dossiers</option>
                {getFolders().map((folder) => (
                  <option key={folder} value={folder}>
                    {folder}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Liste des documents */}
        <Grid container spacing={2}>
          {filteredDocuments.map((doc) => (
            <Grid item xs={12} md={6} lg={4} key={doc.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="h6" component="h2" sx={{ fontSize: '1.1rem' }}>
                        {getFileIcon(doc.current_version.file_type)} {doc.title}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, doc.id)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                  {doc.description && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {doc.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip label={getCategoryLabel(doc.category)} size="small" color="primary" variant="outlined" />
                    <Chip label={getStatusLabel(doc.status)} size="small" color="success" />
                    {doc.folder && (
                      <Chip
                        icon={<FolderIcon />}
                        label={doc.folder}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      Version {doc.current_version.version} • {formatFileSize(doc.current_version.file_size)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {doc.download_count} téléchargements
                    </Typography>
                  </Box>
                  {doc.tags.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {doc.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewDetails(doc)}>
                    Détails
                  </Button>
                  <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload(doc)}>
                    Télécharger
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredDocuments.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" color="textSecondary">
              Aucun document trouvé
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Essayez de modifier vos critères de recherche
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Menu contextuel */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedDocId && (
          <>
            <MenuItem
              onClick={() => {
                const doc = documents.find((d) => d.id === selectedDocId);
                if (doc) handleViewDetails(doc);
              }}
            >
              <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
              Voir les détails
            </MenuItem>
            <MenuItem
              onClick={() => {
                const doc = documents.find((d) => d.id === selectedDocId);
                if (doc) handleDownload(doc);
              }}
            >
              <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
              Télécharger
            </MenuItem>
            <MenuItem
              onClick={() => {
                const doc = documents.find((d) => d.id === selectedDocId);
                if (doc) handleViewVersions(doc);
              }}
            >
              <HistoryIcon sx={{ mr: 1 }} fontSize="small" />
              Historique des versions
            </MenuItem>
            {canManageDocuments() && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    const doc = documents.find((d) => d.id === selectedDocId);
                    if (doc) handleEdit(doc);
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} fontSize="small" />
                  Modifier
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (selectedDocId) {
                      handleDeleteDocument(selectedDocId);
                    }
                  }}
                >
                  <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                  Supprimer
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* Dialog Détails */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedDocument?.title}
          {selectedDocument && (
            <Chip
              label={getStatusLabel(selectedDocument.status)}
              color="success"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box sx={{ mt: 2 }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Informations" />
                <Tab label="Versions" />
              </Tabs>

              {tabValue === 0 && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Description
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedDocument.description || 'Aucune description'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Catégorie
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {getCategoryLabel(selectedDocument.category)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Dossier
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedDocument.folder || 'Aucun dossier'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Accès
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {getAccessLabel(selectedDocument.access_level)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Version actuelle
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedDocument.current_version.version}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Taille du fichier
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {formatFileSize(selectedDocument.current_version.file_size)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Téléchargements
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedDocument.download_count}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {selectedDocument.tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="textSecondary">
                        Créé par
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedDocument.created_by_name} le{' '}
                        {new Date(selectedDocument.created_at).toLocaleDateString('fr-FR')}
                      </Typography>
                      {selectedDocument.approved_by && (
                        <>
                          <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
                            Approuvé par
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {selectedDocument.approved_by_name} le{' '}
                            {selectedDocument.approved_at &&
                              new Date(selectedDocument.approved_at).toLocaleDateString('fr-FR')}
                          </Typography>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tabValue === 1 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Historique des Versions
                  </Typography>
                  <List>
                    {selectedDocument.versions.map((version) => (
                      <ListItem key={version.id}>
                        <ListItemIcon>
                          {version.is_current ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <FileIcon />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">
                                Version {version.version}
                              </Typography>
                              {version.is_current && (
                                <Chip label="Actuelle" size="small" color="success" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {version.file_name} • {formatFileSize(version.file_size)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Uploadé par {version.uploaded_by_name} le{' '}
                                {new Date(version.uploaded_at).toLocaleDateString('fr-FR')}
                              </Typography>
                              {version.change_log && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                  {version.change_log}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = version.file_url;
                              link.download = version.file_name;
                              link.click();
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Fermer</Button>
          {selectedDocument && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => selectedDocument && handleDownload(selectedDocument)}
            >
              Télécharger Version Actuelle
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Versions */}
      <Dialog
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Historique des Versions</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <List>
              {selectedDocument.versions.map((version) => (
                <ListItem key={version.id}>
                  <ListItemIcon>
                    {version.is_current ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <FileIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={`Version ${version.version}`}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {version.file_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(version.uploaded_at).toLocaleDateString('fr-FR')} •{' '}
                          {version.uploaded_by_name}
                        </Typography>
                        {version.change_log && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                            {version.change_log}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = version.file_url;
                        link.download = version.file_name;
                        link.click();
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Upload Document */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          if (!uploading) {
            setUploadDialogOpen(false);
            setUploadForm({
              title: '',
              description: '',
              category: 'other',
              folder: '',
              access_level: 'all',
              tags: '',
              file: null,
            });
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Uploader un Nouveau Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre du document"
                required
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  label="Catégorie"
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value as DocumentCategory })}
                >
                  <MenuItem value="guidelines">Guidelines de Marque</MenuItem>
                  <MenuItem value="templates">Modèles</MenuItem>
                  <MenuItem value="procedures">Procédures</MenuItem>
                  <MenuItem value="pricing">Tarifs</MenuItem>
                  <MenuItem value="other">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dossier (optionnel)"
                value={uploadForm.folder}
                onChange={(e) => setUploadForm({ ...uploadForm, folder: e.target.value })}
                placeholder="Ex: Brand Guidelines"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Niveau d'accès</InputLabel>
                <Select
                  label="Niveau d'accès"
                  value={uploadForm.access_level}
                  onChange={(e) => setUploadForm({ ...uploadForm, access_level: e.target.value as DocumentAccess })}
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="agence">Agence uniquement</MenuItem>
                  <MenuItem value="soebra">SOBEBRA uniquement</MenuItem>
                  {isAdmin && <MenuItem value="admin">Administrateur uniquement</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tags (séparés par des virgules)"
                value={uploadForm.tags}
                onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                placeholder="Ex: marque, logo, template"
                helperText="Séparez les tags par des virgules"
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  {uploadForm.file ? uploadForm.file.name : 'Cliquez pour sélectionner un fichier'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Formats supportés: PDF, Word, Excel, Images, Vidéos
                </Typography>
                {uploadForm.file && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    Taille: {formatFileSize(uploadForm.file.size)}
                  </Typography>
                )}
                <input
                  id="file-upload"
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.mp4,.mov"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadForm({ ...uploadForm, file });
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!uploading) {
                setUploadDialogOpen(false);
                setUploadForm({
                  title: '',
                  description: '',
                  category: 'other',
                  folder: '',
                  access_level: 'all',
                  tags: '',
                  file: null,
                });
              }
            }}
            disabled={uploading}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={!uploadForm.title || !uploadForm.file || uploading}
            onClick={async () => {
              if (!uploadForm.title || !uploadForm.file) return;

              setUploading(true);
              try {
                // Uploader le fichier vers Supabase Storage (ou simuler pour l'instant)
                // Pour l'instant, on stocke juste les métadonnées
                const filePath = `knowledge/${Date.now()}-${uploadForm.file.name}`;
                
                // Créer le document dans la base de données
                const response = await api.knowledge.create({
                  title: uploadForm.title,
                  description: uploadForm.description || undefined,
                  category: uploadForm.category,
                  folder: uploadForm.folder || undefined,
                  file_name: uploadForm.file.name,
                  file_path: filePath,
                  file_type: uploadForm.file.type || 'application/octet-stream',
                  file_size: uploadForm.file.size,
                  access_level: uploadForm.access_level,
                  tags: uploadForm.tags
                    ? uploadForm.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
                    : [],
                });

                // Recharger les documents
                await loadDocuments();

                // Réinitialiser le formulaire
                setUploadForm({
                  title: '',
                  description: '',
                  category: 'other',
                  folder: '',
                  access_level: 'all',
                  tags: '',
                  file: null,
                });

                setUploadDialogOpen(false);
                alert('Document uploadé avec succès');
              } catch (error: any) {
                console.error('Erreur lors de l\'upload:', error);
                alert('Erreur lors de l\'upload du document: ' + (error.message || 'Erreur inconnue'));
              } finally {
                setUploading(false);
              }
            }}
          >
            {uploading ? 'Upload en cours...' : 'Uploader'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Modifier Document */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (!uploading) {
            setEditDialogOpen(false);
            setSelectedDocument(null);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Modifier le Document</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre du document"
                required
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  label="Catégorie"
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value as DocumentCategory })}
                >
                  <MenuItem value="guidelines">Guidelines de Marque</MenuItem>
                  <MenuItem value="templates">Modèles</MenuItem>
                  <MenuItem value="procedures">Procédures</MenuItem>
                  <MenuItem value="pricing">Tarifs</MenuItem>
                  <MenuItem value="other">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dossier (optionnel)"
                value={editForm.folder}
                onChange={(e) => setEditForm({ ...editForm, folder: e.target.value })}
                placeholder="Ex: Brand Guidelines"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Niveau d'accès</InputLabel>
                <Select
                  label="Niveau d'accès"
                  value={editForm.access_level}
                  onChange={(e) => setEditForm({ ...editForm, access_level: e.target.value as DocumentAccess })}
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="agence">Agence uniquement</MenuItem>
                  <MenuItem value="soebra">SOBEBRA uniquement</MenuItem>
                  <MenuItem value="admin">Administrateur uniquement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tags (séparés par des virgules)"
                value={editForm.tags}
                onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                placeholder="Ex: marque, logo, identité"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setSelectedDocument(null);
          }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            disabled={!editForm.title || uploading}
            onClick={handleUpdateDocument}
          >
            {uploading ? 'Modification en cours...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KnowledgeBase;

